import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import ApprovalItemsStatus from '@/components/Approval/@components/ApprovalItemsStatus';
import {
  countCostQuotationCards,
  CostQuotationSnapshot,
  type CostQuotationCardRef,
} from '@/components/Approval/@components/CostQuotation';
import usePartialReject, {
  getPartialRejectErrorMessage,
} from '@/hooks/approval/usePartialReject';
import useApprovalView from '@/hooks/approval/useApprovalView';
import { useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import {
  COST_QUOTATION_REJECT_REASON_MAX_LENGTH,
  parseCostQuotationContent,
} from '@/utils/approval/costQuotation';
import { isCurrentApproverMeOf } from '../../actionVisibility';
import ReasonInputModal from '../../ReasonInputModal';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

/**
 * 121 GERP 원가 견적서 — 저장 본문 그대로(레거시 문서 포함). GERP 재조회 안 함.
 * 부분반려 버튼: 진행중 + 지금 내 결재 차례 + 카드 2장 이상(레거시 조건)
 */
const CostQuotationSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const myInfo = useAtomValue(MyInfoState);
  const { data: view } = useApprovalView(data.documentId);
  const { partialReject, submitting } = usePartialReject(data.documentId);
  // 닫히는 동안 제목이 비지 않게 대상은 남겨 둠
  const [target, setTarget] = useState<CostQuotationCardRef | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const content = parseCostQuotationContent(data.attributes);

  const canPartialReject =
    !!view &&
    view.status === 'P' &&
    isCurrentApproverMeOf(
      view.approvers,
      view.currentApproverOrder,
      myInfo?.passportId ?? '',
      view.status,
    ) &&
    countCostQuotationCards(content) >= 2;

  const renderCardAction = useCallback(
    (card: CostQuotationCardRef) =>
      canPartialReject && card.aprvId ? (
        <BoxButton
          type="button"
          variant="secondary"
          size="small"
          onClick={() => {
            setTarget(card);
            setIsModalOpen(true);
          }}
        >
          {t('부분반려')}
        </BoxButton>
      ) : null,
    [canPartialReject, t],
  );

  const handleSubmit = async (reason: string) => {
    if (!target) return;
    try {
      await partialReject(target.aprvId, reason);
      setIsModalOpen(false);
      showSuccess({ message: t('부분 반려가 완료 되었습니다.') });
    } catch (error) {
      showError({ message: t(getPartialRejectErrorMessage(error)) });
    }
  };

  if (!content) return null;
  return (
    <ContentSlotRoot>
      <ApprovalItemsStatus documentId={data.documentId} />
      <CostQuotationSnapshot
        content={content}
        renderCardAction={renderCardAction}
      />
      <ReasonInputModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
        title={t('{{partcode}} {{aprvRev}}차 반려', {
          partcode: target?.partcode ?? '',
          aprvRev: target?.aprvRev ?? '',
        })}
        submitLabel={t('반려처리')}
        reasonLabel={t('사유입력')}
        reasonPlaceholder=""
        reasonMaxLength={COST_QUOTATION_REJECT_REASON_MAX_LENGTH}
      />
    </ContentSlotRoot>
  );
};

export default CostQuotationSlot;
